import clsx from 'clsx';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@utils/index';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { FormStepID } from '@features/onboarding/enums/formSections.enum';

export const Footer = () => {
  const { currentStep } = useOnboardingStore();
  const formSteps = useFormSteps();
  const { handleJumpTo } = useFormControls();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const location = useLocation();

  const isHome = location.pathname === '/';

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  const handleStepClick = (stepIndex: number, sectionName: string) => {
    if (stepIndex < currentStep) {
      handleJumpTo(sectionName);
    }
  };

  useEffect(() => {
    checkScroll();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, [formSteps]);

  const hiddenSteps = [FormStepID.CoverageStartDate];

  return (
    <footer
      id="app-footer"
      className={cn(
        'w-full fixed bottom-0 left-0 pb-6 z-20 max-2xl:hidden',
        // currentStep ? 'max-lg:hidden' : 'max-2xl:hidden'
      )}
    >
      <div className="max-w-[1920px] px-6 mx-auto">
        <div className="relative w-full flex items-center mx-auto rounded-full bg-white border-gray-200 px-6 text-sm text-gray-600 h-16">
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-0 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 z-10"
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto scrollbar-hide w-full"
            style={{ scrollBehavior: 'smooth' }}
          >
            <div className="flex w-full justify-between gap-4 sm:gap-8">
              {formSteps.filter((step) => !hiddenSteps.includes(step.id as FormStepID)).map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;
                const isClickable = isCompleted;

                return (
                  <div
                    key={step.id}
                    className={clsx(
                      'flex items-center gap-1 whitespace-nowrap',
                      isCompleted && 'text-primary',
                      isCurrent && isHome && 'font-semibold text-black',
                      isClickable && isHome && 'cursor-pointer hover:opacity-80 transition-opacity'
                    )}
                    onClick={() => handleStepClick(index, step.sectionName)}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        handleStepClick(index, step.sectionName);
                      }
                    }}
                    aria-label={isClickable ? `Go back to ${step.sectionName}` : undefined}
                  >
                    <span className="text-xs text-[#a6a5a0] font-normal pr-3">
                      Step {index + 1}
                    </span>
                    <span className="text-xs text-[#a6a5a0]">
                      |
                    </span>
                    <span
                      className={clsx(
                        'text-base pl-3',
                        index > currentStep || !isHome ? 'text-[#a6a5a0]' : ''
                      )}
                    >
                      {step.sectionName}
                    </span>
                    {isCompleted && isHome && (
                      <span className="text-primary font-black">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          id="Layer_1"
                          version="1.1"
                          viewBox="0 0 49 49.89"
                          width={20}
                          height={20}
                        >
                          <polyline
                            fill="none"
                            stroke="#710036"
                            style={{stroke: 'var(--color-primary)'}}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="6"
                            points="17.08 26.72 23.53 33.83 35.08 16.06"
                          />
                        </svg>
                      </span>
                    )}
                    {isCurrent && isHome && step.sectionName !== 'Finish Up' && (
                      <span className="ml-1">
                        <AnimatedDots />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canScrollRight && (
            <button
              onClick={scrollRight}
              className="absolute right-0 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 z-10"
              aria-label="Scroll right"
            >
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </footer>
  );
};
